export type LocalPrintRelayToolPlatform = 'windows' | 'macos';
export type LocalPrintRelayClientKind = 'windows' | 'macos' | 'ios' | 'android' | 'unsupported';

export interface LocalPrintRelayToolDescriptor {
    platform: LocalPrintRelayToolPlatform;
    fileName: string;
    extension: string;
    macArchitecture?: 'arm64' | 'x64';
}

export interface LocalPrintRelayPlatformInfo {
    clientKind: LocalPrintRelayClientKind;
    isDownloadSupported: boolean;
    descriptor: LocalPrintRelayToolDescriptor | null;
}

function getNavigator(): Navigator | undefined {
    return typeof window !== 'undefined' ? window.navigator : undefined;
}

function getUserAgent(navigatorRef: Navigator | undefined): string {
    return navigatorRef?.userAgent || '';
}

export function isIosOrIpadClient(navigatorRef: Navigator | undefined = getNavigator()): boolean {
    const userAgent = getUserAgent(navigatorRef);

    if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return true;
    }

    return navigatorRef?.platform === 'MacIntel' && (navigatorRef?.maxTouchPoints ?? 0) > 1;
}

export function isAndroidClient(navigatorRef: Navigator | undefined = getNavigator()): boolean {
    return /Android/i.test(getUserAgent(navigatorRef));
}

export function isMacOsDesktopClient(navigatorRef: Navigator | undefined = getNavigator()): boolean {
    if (!navigatorRef || isIosOrIpadClient(navigatorRef)) {
        return false;
    }

    const platform = navigatorRef.platform || '';
    const userAgent = getUserAgent(navigatorRef);

    return /Mac/i.test(platform) || /Mac OS X/i.test(userAgent);
}

export function isWindowsClient(navigatorRef: Navigator | undefined = getNavigator()): boolean {
    const platform = navigatorRef?.platform || '';
    const userAgent = getUserAgent(navigatorRef);

    return /Win/i.test(platform) || /Windows/i.test(userAgent);
}

export function detectMacRelayArchitecture(navigatorRef: Navigator | undefined = getNavigator()): 'arm64' | 'x64' {
    const userAgent = getUserAgent(navigatorRef);

    if (/Intel Mac OS X/i.test(userAgent)) {
        return 'x64';
    }

    return 'arm64';
}

export function getLocalPrintRelayPlatformInfo(
    navigatorRef: Navigator | undefined = getNavigator(),
): LocalPrintRelayPlatformInfo {
    if (isIosOrIpadClient(navigatorRef)) {
        return {
            clientKind: 'ios',
            isDownloadSupported: false,
            descriptor: null,
        };
    }

    if (isAndroidClient(navigatorRef)) {
        return {
            clientKind: 'android',
            isDownloadSupported: false,
            descriptor: null,
        };
    }

    if (isMacOsDesktopClient(navigatorRef)) {
        const macArchitecture = detectMacRelayArchitecture(navigatorRef);

        return {
            clientKind: 'macos',
            isDownloadSupported: true,
            descriptor: {
                platform: 'macos',
                fileName: macArchitecture === 'arm64' ? 'print-relay-macos-arm64' : 'print-relay-macos',
                extension: '',
                macArchitecture,
            },
        };
    }

    if (isWindowsClient(navigatorRef)) {
        return {
            clientKind: 'windows',
            isDownloadSupported: true,
            descriptor: {
                platform: 'windows',
                fileName: 'Up2Boat-PrintRelay',
                extension: 'exe',
            },
        };
    }

    return {
        clientKind: 'unsupported',
        isDownloadSupported: false,
        descriptor: null,
    };
}

export function isLocalPrintRelayDownloadSupported(
    navigatorRef: Navigator | undefined = getNavigator(),
): boolean {
    return getLocalPrintRelayPlatformInfo(navigatorRef).isDownloadSupported;
}

export function getLocalPrintRelayToolDescriptor(
    navigatorRef: Navigator | undefined = getNavigator(),
): LocalPrintRelayToolDescriptor {
    const platformInfo = getLocalPrintRelayPlatformInfo(navigatorRef);

    if (platformInfo.descriptor) {
        return platformInfo.descriptor;
    }

    return {
        platform: 'windows',
        fileName: 'Up2Boat-PrintRelay',
        extension: 'exe',
    };
}
